/**
 * Copyright © Smartex Canada All rights reserved.
 * See LICENSE for license details.
 */

import type { SpecificEventListener } from "@/types";
import { getComponentConfig, getComponentElements } from "@/utils/dom/component";
import { getUrlSearchParams } from "@/utils/strings";

export function initProductToolbar() {
    getComponentElements(Toolbar.selector).forEach(element => new Toolbar(element));
}

export class Toolbar {
    static selector = 'product-list-toolbar';

    static defaults: Partial<ToolbarConfig> = {
        page: 'p',
        order: 'sort',
        limit: 'limit',
        loadingClass: 'loading'
    };

    readonly selectors = {
        sorter: '[data-role="sorter"]',
        limiter: '[data-role="limiter"]'
    };

    public element: HTMLElement;
    public config: ToolbarConfig;

    private sorterElement!: HTMLSelectElement | null;
    private limiterElement!: HTMLSelectElement | null;

    private sorterChangeListener?: SpecificEventListener<'change'>;
    private limiterChangeListener?: SpecificEventListener<'change'>;

    constructor(element: HTMLElement, options: Partial<ToolbarConfig> = {}) {
        this.element = element;

        this.config = {
            ...Toolbar.defaults,
            ...getComponentConfig(Toolbar.selector, this.element),
            ...options
        };

        this.initSorter();
        this.initLimiter();
    }

    /**
     * Destroy instance
     */
    public destroy(): void {
        this.sorterElement?.removeEventListener('change', this.sorterChangeListener!);
        this.limiterElement?.removeEventListener('change', this.limiterChangeListener!);

        this.element = null as any;
        this.sorterElement = null as any;
        this.limiterElement = null as any;
    }

    /**
     * Initialize sorter
     */
    private initSorter() {
        const sorter = this.element.querySelector<HTMLSelectElement>(this.selectors.sorter);
        this.sorterElement = sorter;

        if (sorter) {
            this.sorterChangeListener = () => this.changeUrl(
                this.config.order,
                sorter.options[sorter.selectedIndex].value,
                this.config.orderDefault
            );
            sorter.addEventListener('change', this.sorterChangeListener);
        }
    }

    /**
     * Initialize limiter
     */
    private initLimiter() {
        const limiter = this.element.querySelector<HTMLSelectElement>(this.selectors.limiter);
        this.limiterElement = limiter;

        if (limiter) {
            this.limiterChangeListener = () => this.changeUrl(
                this.config.limit,
                limiter.options[limiter.selectedIndex].value,
                this.config.limitDefault
            );
            limiter.addEventListener('change', this.limiterChangeListener);
        }
    }

    /**
     * Update url with
     */
    public changeUrl(paramName: string, paramValue: string | number, defaultValue?: string | number) {
        const baseUrl = this.config.url.split('?')[0];
        const paramData = getUrlSearchParams();
        const currentPage = this.getCurrentPage();
        let newPage;

        if (currentPage > 1 && paramName === this.config.limit) {
            newPage = Math.floor(this.getCurrentLimit() * (currentPage - 1) / (paramValue as number)) + 1;

            if (newPage > 1) {
                paramData.set(this.config.page, newPage.toString())
            } else {
                paramData.delete(this.config.page);
            }
        }

        paramData.set(paramName, paramValue as string);

        if (paramValue == defaultValue) {
            paramData.delete(paramName);
        }

        this.element.classList.add(this.config.loadingClass);

        const params = paramData.toString();
        location.href = baseUrl + (params ? '?' + params : '');
    }

    /**
     * Retrieve current page
     */
    public getCurrentPage(): number
    {
        const page = getUrlSearchParams().get(this.config.page);
        return page ? parseInt(page, 10) : 1;
    }

    /**
     * Retrieve current limit
     */
    public getCurrentLimit(): number
    {
        const limit = getUrlSearchParams().get(this.config.limit);
        return limit ? parseInt(limit, 10) : this.config.limitDefault;
    }
}

export interface ToolbarConfig {
    page: string
    order: string
    limit: string
    orderDefault: string
    limitDefault: number
    url: string
    formKey: string,
    loadingClass: string
}
